/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Mutable state for the buyer
 * board: approval states the buyer set, lines added from nudges, released lines
 * and the POs created from them. Resets on page reload or through
 * `resetBuyerBoardDemo()`.
 */
import { createDemoStore } from '@/demo/core/create-demo-store';
import { getSupplier } from '@/demo/procurement/data/suppliers';
import {
  SEED_OPEN_DRAFTS,
  FIRST_RELEASED_PO_SEQ,
  poNumberFor,
} from './buyer-board-data';
import {
  initialGates,
  nextTier,
  pendingGate,
  tierApproverName,
} from './buyer-board-logic';
import type {
  ApprovalAction,
  ApprovalState,
  BoardLine,
  BuyerId,
  OpenDraftPo,
  PoEvent,
  PoGate,
  PoLineSnapshot,
  ReleasedPo,
  ShipToId,
} from './buyer-board-types';
import type { SupplierId } from '@/demo/procurement/data/types';

export interface BuyerBoardState {
  autoApprove: boolean;
  /** States the buyer set by hand, by line id. Absent means the seed state. */
  stateOverrides: Record<string, ApprovalState>;
  /** Forward buys and AI-suggested lines added after the engine ran. */
  addedLines: BoardLine[];
  /** Released line id to the PO number it went to. */
  released: Record<string, string>;
  /** POs created or updated from the board in this session, newest first. */
  purchaseOrders: ReleasedPo[];
  openDrafts: OpenDraftPo[];
  nextPoSeq: number;
  /** PO the status banner follows. */
  bannerPoNumber: string | null;
}

function seedState(): BuyerBoardState {
  return {
    autoApprove: false,
    stateOverrides: {},
    addedLines: [],
    released: {},
    purchaseOrders: [],
    openDrafts: SEED_OPEN_DRAFTS.map((draft) => ({ ...draft })),
    nextPoSeq: FIRST_RELEASED_PO_SEQ,
    bannerPoNumber: null,
  };
}

const store = createDemoStore<BuyerBoardState>(seedState);

const pendingTimers = new Set<ReturnType<typeof setTimeout>>();

function clearTimers(): void {
  for (const timer of pendingTimers) clearTimeout(timer);
  pendingTimers.clear();
}

function later(ms: number, run: () => void): void {
  const timer = setTimeout(() => {
    pendingTimers.delete(timer);
    run();
  }, ms);
  pendingTimers.add(timer);
}

export function useBuyerBoardState(): BuyerBoardState {
  return store.useStore((state) => state);
}

export function getBuyerBoardState(): BuyerBoardState {
  return store.getState();
}

export function setAutoApprove(on: boolean): void {
  store.update((draft) => {
    draft.autoApprove = on;
  });
}

const ACTION_STATE: Record<
  Exclude<ApprovalAction, 'restore'>,
  ApprovalState
> = {
  approved: 'approved',
  unapproved: 'unapproved',
  trouble: 'trouble',
  rejected: 'rejected',
};

export function applyLineAction(lineId: string, action: ApprovalAction): void {
  store.update((draft) => {
    if (action === 'restore') {
      delete draft.stateOverrides[lineId];
      return;
    }
    draft.stateOverrides[lineId] = ACTION_STATE[action];
  });
}

/** Adds a forward buy or an AI-suggested line to its cart, replacing one with the same id. */
export function addCartLine(line: BoardLine): void {
  store.update((draft) => {
    draft.addedLines = [
      ...draft.addedLines.filter((existing) => existing.id !== line.id),
      line,
    ];
  });
}

export function removeCartLine(lineId: string): void {
  store.update((draft) => {
    draft.addedLines = draft.addedLines.filter((line) => line.id !== lineId);
    delete draft.stateOverrides[lineId];
  });
}

/** Removes the forward buys in a cart and leaves other added lines alone. */
export function removeCartForwardBuys(cartId: string): void {
  store.update((draft) => {
    const removed = draft.addedLines.filter(
      (line) => line.cartId === cartId && line.forwardBuy !== null,
    );
    const removedIds = new Set(removed.map((line) => line.id));
    draft.addedLines = draft.addedLines.filter(
      (line) => !removedIds.has(line.id),
    );
    for (const line of removed) delete draft.stateOverrides[line.id];
  });
}

export type ReleaseTarget =
  | { kind: 'new' }
  | { kind: 'append'; poNumber: string };

export interface ReleaseInput {
  cartId: string;
  supplierId: SupplierId;
  shipToId: ShipToId;
  buyerId: BuyerId;
  lines: PoLineSnapshot[];
  target: ReleaseTarget;
}

function sumLines(lines: readonly PoLineSnapshot[]): number {
  return (
    Math.round(lines.reduce((sum, line) => sum + line.extCost, 0) * 100) / 100
  );
}

/** Sent to the supplier, then acknowledged, on a short script. */
function scheduleDelivery(poNumber: string, supplierId: SupplierId): void {
  const supplier = getSupplier(supplierId).shortName;
  later(900, () => {
    store.update((draft) => {
      const po = draft.purchaseOrders.find((p) => p.poNumber === poNumber);
      if (po === undefined || po.status !== 'Approved') return;
      po.status = 'Sent';
      po.events.push({
        at: Date.now(),
        label: `Sent to ${supplier} electronically`,
      });
    });
  });
  later(2_300, () => {
    store.update((draft) => {
      const po = draft.purchaseOrders.find((p) => p.poNumber === poNumber);
      if (po === undefined || po.status !== 'Sent') return;
      po.acknowledged = true;
      po.events.push({
        at: Date.now(),
        label: 'Order confirmed by supplier in 2 h',
      });
    });
  });
}

/**
 * Creates a draft PO from the approved lines (or adds them to an open draft),
 * runs it through the approval tiers and marks the lines released. Returns the
 * PO number.
 */
export function releaseLines(input: ReleaseInput): string {
  let poNumber = '';
  store.update((draft) => {
    const added = sumLines(input.lines);
    const buyCount = input.lines.filter((line) => line.kind === 'buy').length;
    const now = Date.now();
    let total = added;
    let appendedLineCount = 0;
    const events: PoEvent[] = [];
    if (input.target.kind === 'append') {
      const target = input.target;
      const open = draft.openDrafts.find((d) => d.poNumber === target.poNumber);
      poNumber = target.poNumber;
      if (open !== undefined) {
        total = Math.round((open.total + added) * 100) / 100;
        draft.openDrafts = draft.openDrafts.filter(
          (d) => d.poNumber !== target.poNumber,
        );
      }
      appendedLineCount = buyCount;
      events.push({
        at: now,
        label: `${buyCount} ${buyCount === 1 ? 'line' : 'lines'} added to open ${poNumber}`,
      });
    } else {
      poNumber = poNumberFor(draft.nextPoSeq);
      draft.nextPoSeq += 1;
      events.push({
        at: now,
        label: `Draft ${poNumber} created from ${buyCount} approved ${buyCount === 1 ? 'line' : 'lines'}`,
      });
    }
    const gates: PoGate[] = initialGates(total, input.buyerId);
    const pending = pendingGate(gates);
    if (pending === null) {
      events.push({ at: now, label: 'Auto-approved within the buyer limit' });
    } else {
      events.push({
        at: now,
        label: `Sent for approval to ${pending.approverName} (${pending.role})`,
      });
    }
    const po: ReleasedPo = {
      poNumber,
      cartId: input.cartId,
      supplierId: input.supplierId,
      shipToId: input.shipToId,
      buyerId: input.buyerId,
      total,
      lines: input.lines,
      status: pending === null ? 'Approved' : 'Pending Approval',
      gates,
      appendedLineCount,
      acknowledged: false,
      createdAt: now,
      events,
    };
    draft.purchaseOrders = [po, ...draft.purchaseOrders];
    for (const line of input.lines) draft.released[line.lineId] = poNumber;
    draft.bannerPoNumber = poNumber;
  });
  const created = store
    .getState()
    .purchaseOrders.find((po) => po.poNumber === poNumber);
  if (created !== undefined && created.status === 'Approved') {
    scheduleDelivery(poNumber, input.supplierId);
  }
  return poNumber;
}

export function simulateApproval(poNumber: string): void {
  const existing = store
    .getState()
    .purchaseOrders.find((po) => po.poNumber === poNumber);
  if (existing === undefined || existing.status !== 'Pending Approval') return;
  store.update((draft) => {
    const po = draft.purchaseOrders.find((p) => p.poNumber === poNumber);
    if (po === undefined) return;
    const gate = pendingGate(po.gates);
    if (gate === null) return;
    const now = Date.now();
    gate.status = 'Approved';
    gate.note = `Approved by ${gate.approverName}`;
    gate.decidedAt = now;
    po.status = 'Approved';
    po.events.push({
      at: now,
      label: `Approved by ${gate.approverName} (${gate.role})`,
    });
    draft.bannerPoNumber = poNumber;
  });
  scheduleDelivery(poNumber, existing.supplierId);
}

/** The approver did not respond in time: the gate times out and escalates up one tier. */
export function simulateTimeout(poNumber: string): void {
  store.update((draft) => {
    const po = draft.purchaseOrders.find((p) => p.poNumber === poNumber);
    if (po === undefined || po.status !== 'Pending Approval') return;
    const gate = pendingGate(po.gates);
    if (gate === null) return;
    const next = nextTier(gate.tierId);
    if (next === null) return;
    const now = Date.now();
    gate.status = 'Timed out';
    gate.note = 'No decision after 24 hours';
    gate.decidedAt = now;
    const approverName = tierApproverName(next, po.buyerId);
    po.gates.push({
      tierId: next.id,
      role: next.role,
      approverName,
      status: 'Escalated',
      viaEscalation: true,
      note: `Escalated from ${gate.role}`,
      decidedAt: null,
    });
    po.events.push({
      at: now,
      label: `${gate.approverName} did not respond; escalated to ${approverName} (${next.role})`,
    });
    draft.bannerPoNumber = poNumber;
  });
}

export function simulateRejection(poNumber: string): void {
  store.update((draft) => {
    const po = draft.purchaseOrders.find((p) => p.poNumber === poNumber);
    if (po === undefined || po.status !== 'Pending Approval') return;
    const gate = pendingGate(po.gates);
    if (gate === null) return;
    const now = Date.now();
    gate.status = 'Rejected';
    gate.note = `Rejected by ${gate.approverName}`;
    gate.decidedAt = now;
    po.status = 'Cancelled';
    po.events.push({
      at: now,
      label: `Rejected by ${gate.approverName} (${gate.role}); returned to the buyer`,
    });
    draft.bannerPoNumber = poNumber;
  });
}

export function dismissBanner(): void {
  store.update((draft) => {
    draft.bannerPoNumber = null;
  });
}

export function resetBuyerBoardDemo(): void {
  clearTimers();
  store.reset();
}
